import AttachFileIcon from "@mui/icons-material/AttachFile";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import {
  Box,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import type { EmailIntelligenceConfig } from "adl-gen/ferovinum/app/config";
import { useUiConfig } from "adl-service/hooks/use-ui-config";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useAlert } from "../../context/global-alert/use-alert-context";

export enum EmailSourceReferenceType {
  SaleOrder = "saleOrder",
  NewDealRequest = "newDealRequest",
  PurchaseRequest = "purchaseRequest",
  ProductionOrder = "productionOrder",
  Workflow = "workflow",
}

export interface EmailsPanelProps {
  referenceType: EmailSourceReferenceType;
  reference: string;
  authToken: string;
}
export const EmailsPanel = (props: EmailsPanelProps) => {
  const [messages, setMessages] = useState<Message[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const { emailIntelligenceConfig } = useUiConfig();
  const authToken = props.authToken;
  const emailService = useMemo(
    () => new EmailService(authToken, emailIntelligenceConfig),
    [authToken, emailIntelligenceConfig],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const messages = await emailService.getMessages(
          props.reference,
          props.referenceType,
        );
        setMessages(messages);
      } catch (_error) {
        setMessages(undefined);
      } finally {
        setLoading(false); // Stop loading when done
      }
    };

    fetchData();
  }, [props, emailService]);

  if (loading) {
    return <div>Loading...</div>;
  }
  if (!messages) {
    return (
      <div
        style={{ textAlign: "center", fontSize: "medium", fontWeight: "bold" }}
      >
        Messages Not Available Right Now. Please try again later.
      </div>
    );
  }

  return (
    <div>
      {messages.length > 0 ? (
        <MessageList messages={messages} emailService={emailService} />
      ) : (
        <div
          style={{
            textAlign: "center",
            fontSize: "medium",
            fontWeight: "bold",
          }}
        >
          No Messages
        </div>
      )}
    </div>
  );
};

interface Message {
  messageId: string;
  threadId: string;
  date: number;
  from: string;
  to: string;
  subject: string;
  text: string;
  html: string;
  hasAttachments: boolean;
}

interface MessageAttachmentMeta {
  attachmentId: string;
  contentType: string;
  filename: string;
}

const MessageList = ({
  messages,
  emailService,
}: {
  messages: Message[];
  emailService: EmailService;
}) => {
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [attachments, setAttachments] = useState<MessageAttachmentMeta[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showAlert] = useAlert();

  useEffect(() => {
    if (selectedMessage?.hasAttachments) {
      emailService
        .getAttachments(selectedMessage.messageId)
        .then((attachments) => {
          setAttachments(attachments);
        });
    } else {
      setAttachments([]);
    }
  }, [selectedMessage, emailService]);

  const [selectedAttachmentUrl, setSelectedAttachmentUrl] =
    useState<string>("about:blank");

  const handleIconClick = async (
    _event: React.MouseEvent<HTMLElement>,
    attachment: MessageAttachmentMeta,
  ) => {
    const { contentType, attachmentId } = attachment;

    if (contentType === "text/calendar" || contentType === "application/ics") {
      showAlert({
        title: "Cannot Open",
        body: "Sorry, Calendar Invitations cannot be opened here.",
      });
      return;
    }

    setSelectedAttachmentUrl("about:blank");

    emailService.getAttachmentUrl(attachmentId).then((url) => {
      if (!url) {
        return;
      }

      if (
        contentType.includes("image") ||
        contentType === "text/plain" ||
        contentType === "application/pdf"
      ) {
        setAnchorEl(document.body);
        setSelectedAttachmentUrl(url);
      } else {
        window.open(url, "_blank")?.focus();

        showAlert({
          title: "Downloaded",
          severity: "info",
          body: "Attachment has been downloaded. Check your downloads folder.",
        });
      }
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getIconForAttachment = (contentType: string) => {
    if (contentType.includes("image") || contentType.includes("image")) {
      return <ImageIcon style={{ color: "blue" }} />;
    }
    if (contentType === "application/pdf") {
      return <PictureAsPdfIcon style={{ color: "red" }} />;
    }
    if (contentType === "text/csv") {
      return <TableChartIcon style={{ color: "green" }} />;
    }
    if (contentType === "text/calendar" || contentType === "application/ics") {
      return <CalendarTodayIcon style={{ color: "maroon" }} />;
    }
    return <FilePresentIcon />;
  };

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <TableContainer
        component={Paper}
        sx={{ maxHeight: 300, overflowY: "auto" }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Subject</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.map((message) => (
              <TableRow
                key={message.messageId}
                onClick={() => setSelectedMessage(message)}
                sx={{
                  backgroundColor:
                    selectedMessage?.messageId === message.messageId
                      ? "rgba(0, 0, 0, 0.08)"
                      : "transparent",
                  cursor: "pointer",
                }}
              >
                {/* Attachments */}
                {message.hasAttachments ? (
                  <TableCell
                    sx={{
                      width: "28px",
                      textAlign: "center",
                    }}
                  >
                    <AttachFileIcon fontSize="small" />
                  </TableCell>
                ) : (
                  <TableCell sx={{ width: "40px" }} />
                )}

                {/* Subject */}
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "37.5%",
                  }}
                >
                  <Tooltip title={message.subject} placement="top">
                    <span>{message.subject}</span>
                  </Tooltip>
                </TableCell>

                {/* Date */}
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                  }}
                >
                  {new Date(message.date).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                  })}
                </TableCell>

                {/* From */}
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                  }}
                >
                  <Tooltip title={message.from} placement="top">
                    <span>{message.from}</span>
                  </Tooltip>
                </TableCell>

                {/* To */}
                <TableCell
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "150px",
                  }}
                >
                  <Tooltip title={message.to} placement="top">
                    <span>{message.to}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {attachments.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: "8px",
            marginTop: "16px",
            border: "1px solid rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
            width: "100%",
            overflowX: "auto",
            padding: "8px",
          }}
        >
          {attachments.map((attachment) => (
            <Box
              key={attachment.attachmentId}
              sx={{ textAlign: "center", minWidth: "100px" }}
            >
              <IconButton
                onClick={(event) => handleIconClick(event, attachment)}
              >
                {getIconForAttachment(attachment.contentType)}
              </IconButton>
              <div>{attachment.filename || "Untitled"}</div>
            </Box>
          ))}
        </Box>
      )}

      {selectedMessage && (
        <iframe
          srcDoc={selectedMessage.html || selectedMessage.text}
          style={{
            width: "100%",
            height: "calc(100vh - 316px)",
            marginTop: "16px",
            border: "none",
          }}
        />
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            width: "80vw",
            height: "80vh",
            position: "relative",
          },
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 0, right: 0 }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <iframe
            src={selectedAttachmentUrl}
            style={{
              width: "90%",
              height: "90%",
              border: "none",
            }}
          />
        </Box>
      </Popover>
    </div>
  );
};

class EmailService {
  private readonly serverRoot: string;

  constructor(
    private readonly authToken: string,
    config: EmailIntelligenceConfig,
  ) {
    this.serverRoot = `//${config.server}:${config.port}`;
  }

  async getMessages(
    reference: string,
    referenceType: EmailSourceReferenceType,
  ): Promise<Message[] | undefined> {
    let query = "";
    switch (referenceType) {
      case EmailSourceReferenceType.SaleOrder:
        query = "messages-by-sale-order";
        break;

      case EmailSourceReferenceType.NewDealRequest:
        query = "messages-by-new-deal-request";
        break;

      case EmailSourceReferenceType.PurchaseRequest:
        query = "messages-by-purchase-request";
        break;

      case EmailSourceReferenceType.ProductionOrder:
        query = "messages-by-production-order";
        break;

      case EmailSourceReferenceType.Workflow:
        query = "messages-by-workflow";
        break;
    }

    if (!query) {
      return;
    }

    const response = await fetch(
      `${this.serverRoot}/rest/${query}?id=${reference}&pageSize=50`,
      {
        method: "GET",
        mode: "cors",
        headers: {
          "X-Auth-Token": this.authToken,
        },
      },
    );

    const data = await response.json();
    if (data.success) {
      const messages: Message[] = data.success.result;
      messages.sort((a: Message, b: Message) => b.date - a.date);
      return messages;
    }
  }

  async getAttachments(messageId: string): Promise<MessageAttachmentMeta[]> {
    const response = await fetch(
      `${this.serverRoot}/rest/message-attachments?messageId=${messageId}`,
      {
        method: "GET",
        mode: "cors",
        headers: {
          "X-Auth-Token": this.authToken,
        },
      },
    );

    const data = await response.json();

    const order = ["application/pdf", "text/csv", "image"];
    const getIndex = (contentType: string) => {
      if (contentType.includes("image")) {
        return order.indexOf("image");
      }

      const index = order.indexOf(contentType);
      return index === -1 ? order.length : index;
    };

    return data.sort(
      (a: MessageAttachmentMeta, b: MessageAttachmentMeta) =>
        getIndex(a.contentType) - getIndex(b.contentType),
    );
  }

  async getAttachmentUrl(attachmentId: string) {
    const response = await fetch(
      `${this.serverRoot}/rest/message-attachment?attachmentId=${attachmentId}`,
      {
        method: "GET",
        mode: "cors",
        headers: {
          "X-Auth-Token": this.authToken,
        },
      },
    );

    try {
      return window.URL.createObjectURL(await response.blob());
    } catch (_e) {
      return null;
    }
  }
}
